// src/api/taxCalculations.ts
// 🔒 All network calls centralized here. No UI changes needed.
// You can reuse these in "add", "edit", and "view" flows.

import lo from "assets/libs/moment/src/locale/lo";
import { safeConsole } from "utils/envUtils";
import { handleApiError } from "utils/errorHandler";
import { getAuthCookie } from "../../utils/authCookies";
import { forcePermissionsFalse, resolveSellerScopeOrganizationId, sanitizeOrganizationId } from "api/salesOrderApi";

export type AddressDetails = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  latitude?: number | string;
  longitude?: number | string;
};

export type ManualShippingInput = {
  // The exact object the user selected in your UI (manual shipping row)
  shippingOption: any; // keep as any to avoid breaking changes in callers
  address: AddressDetails | null;
  quantity: number; // current total qty in cart
  supplierId?: string;
  createdById?: string;
};
// export type ProductTaxInput = {
//   // One product line
//   product: any; // your full variant/product object
//   address: AddressDetails | null;
//   quantity: number;
//   unitPrice: number;
// };
type SpecialFeeRow = {
  specialfee_id: string;
  specialfee_name: string;
  specialfee_amount: number;
  specialfee_tax_percentage: number;
  specialfee_tax_amount: number;
  specialfee_taxable: boolean;
};


export type ShippingCalcResult = {
  shipping__id: string;
  shipping_name: string;
  shipping_amount: number;
  shipping_tax_percentage: number;
  shipping_tax_amount: number;
  shipping_taxable: boolean;
  shipping_delivarydate: string;
  shipping_delivery_time: string;
};

export type UpsShippingInput = {
  upsRate: any;                 // one item from /shipping/ups/rates
  address: any;                 // UI shipping address object (has address_details)
  quantity?: number;            // NOT used to scale amount (rates are already per full packages)
  createdById: string;          // userId in the URL
  integrationId?: string | null;
  supplierId?: string;
};

export type SpecialFeeInput = {
  fee: any;             // entire special-fee object from UI/API
  address: any;         // selected shipping address object
  createdById: string;  // for /created-by/:id endpoints
  supplierId?: string;
};

export type SpecialFeeCalcResult = {
  specialfee_id: string;
  specialfee_name: string;
  specialfee_amount: number;
  specialfee_tax_percentage: number; // 0-100
  specialfee_tax_amount: number;     // money
  specialfee_taxable: boolean;
};

function toAddressPayload(addr: any) {
  const a = addr?.address_details || addr || {};
  return {
    addressLine1: a.addressLine1 || a.line1 || a.address1 || "",
    addressLine2: a.addressLine2 || a.line2 || a.address2 || "",
    city: a.city || a.cityCode || "",
    state: a.state || a.stateCode || "",
    country: a.country || a.countryCode || "United States",
    zip: a.zip || a.postalCode || a.postcode || "",
    cityCode: a.cityCode || "",
    stateCode: a.stateCode || a.state || "",
    countryCode: a.countryCode || "US",
  };
}

const API = process.env.REACT_APP_API_URL as string;

const jsonHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

// const jsonHeaders = (token?: string) => ({
//   Accept: "application/json",
//   "Content-Type": "application/json",
//   ...(token ? { Authorization: `Bearer ${token}` } : {}),
// });
const userId1 = getAuthCookie("userId");
const supplierId = localStorage.getItem("supplierId");
// —————————————————————————————————————————————————————————————
// 1) ACTIVE TAX TYPE
// —————————————————————————————————————————————————————————————
export async function getActiveTaxType(token: string, organizationId?: string, personId?: string): Promise<"manual" | "taxcloud" | "geera" | string> {
  const errorUserId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const orgId = sanitizeOrganizationId(
    organizationId ?? resolveSellerScopeOrganizationId()
  );
  if (!orgId) return "manual";
  const url = `${process.env.REACT_APP_API_URL}${forcePermissionsFalse(`/organizations/${orgId}/tax/active-type`)}`;
  
  try {
    // console.log("getActiveTaxType1", userId, personId)
    const res = await fetch(url, {
      method: "GET",
      headers: jsonHeaders(token),
    });
    const data = await res.json();
    // console.log(data, "dataactive")
    return String(data?.taxType).toLowerCase();
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "manualtax_get_active_tax_type",
      userId: errorUserId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url,
      method: "GET",
      additionalInfo: {
        organizationId: orgId,
        personId,
      },
    }, token);
    return "manual"; // fallback
  }
}

// Helper to decide integration
export async function isTaxCloudActive(token: string) {
  const t = await getActiveTaxType(token);
  return t === "taxcloud";
}
export async function isManualActive(token: string) {
  const t = await getActiveTaxType(token);
  // console.log("isManualActive", t)
  return t === "manual";
}
export async function isGeeraActive(token: string) {
  const t = await getActiveTaxType(token);
  return t === "geera" || t === "gera" || t === "geera_tax"; // flexible
}

// add this helper near the top (maps your Addresses shape → API shape)
function toDestinationAddress(a: any) {
  const src = a?.address_details || a?.address || a || {};
  return {
    addressLine1: String(src.addressLine1 || src.line1 || src.address1 || ""),
    addressLine2: String(src.addressLine2 || src.line2 || src.address2 || ""),
    city: String(src.city || src.cityCode || ""),
    state: String(src.state || src.stateCode || ""),
    country: String(src.country || src.countryCode || "US"),
    zip: String(src.zip || src.postalCode || src.postcode || ""),
    cityCode: String(src.cityCode || src.city || ""),
    stateCode: String(src.stateCode || src.state || ""),
    countryCode: String(src.countryCode || src.country || "US"),
  };
}

function toManualDestination(a: any) {
  const x = a?.address_details || a || {};
  return {
    addressLine1: x.addressLine1 || x.line1 || x.address1 || "",
    addressLine2: x.addressLine2 || x.line2 || x.address2 || "",
    city: x.city || "",
    state: x.state || x.stateCode || "",
    country: x.country || x.countryName || "United States",
    zip: x.zip || x.postalCode || "",
    cityCode: x.cityCode || "",
    stateCode: x.stateCode || x.state || "",
    countryCode: x.countryCode || "US",
  };
}

// Helper to pull a flat address payload your backend expects
const toDest = (addr: any) => {
  const a = addr?.address_details || addr || {};
  return {
    addressLine1: a.addressLine1 || a.line1 || "",
    addressLine2: a.addressLine2 || "",
    city: a.city || "",
    state: a.state || a.stateCode || "",
    country: a.country || a.countryCode || "",
    zip: a.zip || "",
    cityCode: a.cityCode || "",
    stateCode: a.stateCode || "",
    countryCode: a.countryCode || "",
  };
};

async function ensureUpsIntegrationId(token: string, maybeId?: string | null): Promise<string> {
  if (maybeId) return String(maybeId);
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const url = `${API}/shipping/ups/configs`;
  
  try {
    const r = await fetch(url, { headers: jsonHeaders(token) });
    const data = r.ok ? await r.json().catch(() => []) : [];
    const list: any[] = Array.isArray(data) ? data : (data?.data ?? []);
    const id = list?.[0]?.id || list?.[0]?._id || list?.[0]?.integration_id;
    if (!id) throw new Error("No UPS integration found");
    return String(id);
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "manualtax_ensure_ups_integration_id",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url,
      method: "GET",
    }, token);
    throw error;
  }
}

// —————————————————————————————————————————————————————————————
// 2) MANUAL SHIPPING TAX
//    POST /shippingtaxcalculation
// —————————————————————————————————————————————————————————————
// ✅ REPLACE the old function with this exact name/signature
export async function calcManualShippingTax(
  token: string,
  input: ManualShippingInput
): Promise<ShippingCalcResult> {
  if (!input?.shippingOption) throw new Error("Missing manual shipping option");
  const shipping_cost_id =
    String(input.shippingOption?.id || input.shippingOption?._id || input.shippingOption?.code || "").trim();
  if (!shipping_cost_id) throw new Error("Missing shipping_cost_id");

  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const destination_address = toDestinationAddress(input.address);
  const url = `${process.env.REACT_APP_API_URL}/shipping-costs/calculate-tax/created-by/${input.supplierId || input.createdById || ""}`;
  const payload = { shipping_cost_id, destination_address };
  
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: jsonHeaders(token),
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Manual shipping tax calc failed");
    const data = await res.json();
    // console.log(res, "resokshipping")

    // map response → UI row
    const name = String(input.shippingOption?.name || input.shippingOption?.service_name || "");
    const amount = Number(data?.shipping_cost ?? input.shippingOption?.cost ?? input.shippingOption?.amount ?? 0);
    const pct = Number(data?.tax_rate || 0);        // server returns percent (e.g., 10)
    const taxAmt = Number(data?.tax_amount || 0);

    return {
      shipping__id: shipping_cost_id,
      shipping_name: name,
      shipping_amount: amount,
      shipping_tax_percentage: pct,
      shipping_tax_amount: taxAmt,
      shipping_taxable: true,
      shipping_delivarydate: String(input.shippingOption?.deliveryDate || input.shippingOption?.delivery_date || ""),
      shipping_delivery_time: String(input.shippingOption?.delivery_by_time || ""),
    };
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "manualtax_calc_manual_shipping_tax",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url,
      method: "POST",
      requestBody: payload,
      additionalInfo: {
        supplierId: input.supplierId,
        createdById: input.createdById,
        shipping_cost_id,
      },
    }, token);
    throw error;
  }
}

// export async function calcManualShippingTax(
//   token: string,
//   input: ManualShippingInput
// ): Promise<ShippingRow> {
//   const qty = Number(input?.quantity || 1);
//   const opt = input?.shippingOption;
//   if (!opt) throw new Error("Missing manual shipping option");

//   const shipping_cost_id = String(opt.id || opt._id || opt.code || "").trim();
//   if (!shipping_cost_id) throw new Error("Missing shipping_cost_id");

//   const createdById = String(input.createdById || "").trim();  // <— REQUIRED
//   if (!createdById) throw new Error("Missing createdById for created-by route");

//   const destination_address = toDestinationAddress(input.address);

//   const res = await fetch(
//     `${process.env.REACT_APP_API_URL}/shipping-costs/calculate-tax/created-by/${createdById}`,
//     {
//       method: "POST",
//       headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//       body: JSON.stringify({ shipping_cost_id, destination_address }),
//     }
//   );

//   if (!res.ok) throw new Error("Manual shipping tax calc failed");
//   const data = await res.json();

//   const ratePct   = Number(data?.tax_rate || 0);                    // e.g. 10
//   const unitCost  = Number(data?.shipping_cost ?? opt.cost ?? 0);   // base cost
//   const amount    = round2(unitCost * qty);                         // multiply by qty
//   const taxAmt    = round2(amount * (ratePct / 100));               // recompute tax for qty

//   return {
//     shipping__id: shipping_cost_id,
//     shipping_name: String(opt.name || opt.service_name || ""),
//     shipping_amount: amount,
//     shipping_tax_percentage: ratePct,
//     shipping_tax_amount: taxAmt,
//     shipping_taxable: true,
//     shipping_delivarydate: String(opt.deliveryDate || opt.delivery_date || ""),
//     shipping_delivery_time: String(opt.delivery_by_time || ""),
//   };
// }


export const manualcalcManualShippingTax = calcManualShippingTax;


// —————————————————————————————————————————————————————————————
// 3) UPS SHIPPING TAX
//    POST /upsshippingtaxcalculation
// —————————————————————————————————————————————————————————————

// ✅ NEW: UPS tax calc for type=manual
export async function calcUpsShippingTax(token: string, input: UpsShippingInput): Promise<ShippingCalcResult> {
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  
  // console.log("calcUpsShippingTax", input);
  const integrationId = await ensureUpsIntegrationId(token, input.integrationId);
  const dest = toManualDestination(input.address);

  // IMPORTANT: amount comes from the UPS rate (the rate already reflects all packages/qty)
  const meta = input.upsRate || {};
  const amount = Number(meta?.total_charge ?? meta?.cost ?? 0);
  const code = String(meta?.service_code || meta?.code || "");
  const name = String(meta?.service_name || meta?.service || "");

  const url = `${API}/shipping/ups/organizations/${input.supplierId || input.createdById}/configs/${integrationId}/calculate-tax`;
  const body = { shipping_cost: amount, destination_address: dest };

  try {
    const res = await fetch(url, { method: "POST", headers: jsonHeaders(token), body: JSON.stringify(body) });
    if (!res.ok) throw new Error("UPS manual tax calc failed");
    const j = await res.json().catch(() => ({}));

    return {
      shipping__id: code,
      shipping_name: name,
      shipping_amount: amount,
      shipping_tax_percentage: Number(j?.tax_rate || 0),
      shipping_tax_amount: Number(j?.tax_amount || 0),
      shipping_taxable: true,
      shipping_delivarydate: String(meta?.delivery_days || meta?.deliveryDate || ""),
      shipping_delivery_time: String(meta?.delivery_by_time || ""),
    };
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "manualtax_calc_ups_shipping_tax",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url,
      method: "POST",
      requestBody: body,
      additionalInfo: {
        supplierId: input.supplierId,
        createdById: input.createdById,
        integrationId,
      },
    }, token);
    throw error;
  }
}

// ------------- NEW: single-call manual calc for an array of fees -------------
// ✅ NEW: posts "an array of fees" for a single createdById
export async function calcSpecialFeeTax(
  token: string,
  taxType: "manual" | "taxcloud" | "geera",
  inputs: SpecialFeeInput[]
): Promise<SpecialFeeCalcResult[]> {
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  
  // We only have a manual endpoint for now – use it for all three types.
  const createdById = String(inputs?.[0]?.createdById || "");
  const supplerId = String(inputs?.[0]?.supplierId || "");
  const destination_address = toDest(inputs?.[0]?.address);
  // console.log("inputs", inputs);
  const body = {
    destination_address,

    // send the FULL fee objects just like the API contract shows
    special_fees: (inputs || []).map(x => x.fee),
  };

  const url = `${process.env.REACT_APP_API_URL}/special-fees/calculate-tax/organizations/${supplerId || createdById}`;
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  
  // fallback result
  const fallbackResult = (inputs || []).map(x => ({
    specialfee_id: String(x.fee?.id || x.fee?._id || ""),
    specialfee_name: String(x.fee?.name || ""),
    specialfee_amount: Number(x.fee?.amount || 0),
    specialfee_tax_percentage: 0,
    specialfee_tax_amount: 0,
    specialfee_taxable: x.fee?.taxable !== false,
  }));

  try {
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      // fall back to zero-tax rows with the same shape
      return fallbackResult;
    }

    const data = await res.json().catch(() => []);
    // API returns an array like:
    // { fee_id, fee_name, fee_amount, tax_rate, tax_amount, ... }
    return (Array.isArray(data) ? data : []).map((r: any) => ({
      specialfee_id: String(r.specialfee_id ?? r.fee_id ?? r.id ?? ""),
      specialfee_name: String(r.specialfee_name ?? r.fee_name ?? r.name ?? ""),
      specialfee_amount: Number(r.specialfee_amount ?? r.fee_amount ?? r.amount ?? 0),
      // NOTE: your endpoint already returns percentage in whole numbers (e.g. 15), not 0.15
      specialfee_tax_percentage: Number(
        r.specialfee_tax_percentage ?? r.tax_rate ?? 0
      ),
      specialfee_tax_amount: Number(
        r.specialfee_tax_amount ?? r.tax_amount ?? 0
      ),
      specialfee_taxable: (r.tax_amount ?? 0) > 0 || (r.tax_rate ?? 0) > 0,
    }));
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "manualtax_calc_special_fee_tax",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url,
      method: "POST",
      requestBody: body,
      additionalInfo: {
        supplierId: supplerId,
        createdById,
        taxType,
        feeCount: (inputs || []).length,
      },
    }, token);
    // fallback on error
    return fallbackResult;
  }
}


// —————————————————————————————————————————————————————————————
// 4) SPECIAL FEE TAX (multi)
//    POST /specialfeetaxcalculation
// —————————————————————————————————————————————————————————————
// export async function calcSpecialFeeTax(
//   token: string,
//   args: {
//     createdById: string;
//     address: any;
//     fees: any[];               // pass your selected fee objects as-is
//   }
// ): Promise<SpecialFeeRow[]> {
//   const { createdById, address, fees } = args;
//   if (!createdById) throw new Error("createdById is required");
//   const destination_address = toDestination(address);

//   // Build request payload
//   const body = {
//     destination_address,
//     special_fees: (fees || []).map((f) => ({
//       id: String(f.id || f._id || f.fid || ""),
//       name: String(f.name || ""),
//       amount: Number(f.amount || 0),
//       taxable: f?.taxable !== false,
//       // Pass-through rule/group if present on the fee (backend can use them)
//       ...(f.tax_rule ? { tax_rule: f.tax_rule } : {}),
//       ...(f.tax_group ? { tax_group: f.tax_group } : {}),
//     })),
//   };

//   const res = await fetch(`${API}/special-fees/calculate-tax/created-by/${createdById}`, {
//     method: "POST",
//     headers: jsonHeaders(token),
//     body: JSON.stringify(body),
//   });
//   if (!res.ok) throw new Error("Special fee tax calc failed");

//   const data = await res.json().catch(() => ({}));
//   // Try to match response per fee. If server doesn’t echo per-fee rows,
//   // fallback compute from returned tax_rate if present.
//   const list = Array.isArray(data?.results) ? data.results :
//                Array.isArray(data) ? data : [];

//   const byId: Record<string, any> = {};
//   for (const r of list) {
//     const id = String(r?.id || r?.specialfee_id || r?._id || "");
//     byId[id] = r;
//   }

//   return (body.special_fees || []).map((f: any) => {
//     const m = byId[String(f.id)] || {};
//     const rate = Number(m?.tax_rate ?? m?.rate ?? 0);
//     const amt  = Number(m?.tax_amount ?? (Number(f.amount || 0) * (rate / 100)));

//     return {
//       specialfee_id: String(f.id),
//       specialfee_name: String(f.name),
//       specialfee_amount: Number(f.amount || 0),
//       specialfee_tax_percentage: rate,      // percent
//       specialfee_tax_amount: amt,           // money
//       specialfee_taxable: f?.taxable !== false,
//     };
//   });
// }




// —————————————————————————————————————————————————————————————
// 5) PRODUCT TAX
//    POST /produtstaxcalculations
// —————————————————————————————————————————————————————————————
// --- Product tax (batch) -----------------------------------------------

// Request you’ll send from the page
export type ProductTaxBatchItem = {
  product_id: string;
  variant_id: string;
  quantity: number; // qty
  unitPrice: number;
};

export type ProductTaxBatchInput = {
  createdById: string; // signage/supplier id
  supplierId?: string; // optional override signage/supplier id
  destination: any;        // shipping address object (has address_details.*)
  items: ProductTaxBatchItem[];
  token?  : string;    // optional user token override
};

// Line result you’ll get back per product
export type ProductTaxLineResult = {
  product_id: string;
  variant_id: string;
  quantity: number;
  unit_retail_price: number;
  tax_amount: number;
  unit_tax_amount: number;
  tax_percentage: number; // whole percent, e.g. 15
  taxable: boolean;
};

// Normalise address -> API payload
function toDestinationAddress_ForProducts(addr: any) {
  const a = addr?.address_details || addr || {};
  return {
    addressLine1: String(a.addressLine1 || a.address1 || ""),
    addressLine2: String(a.addressLine2 || a.address2 || ""),
    city: String(a.city || a.cityCode || ""),
    state: String(a.state || a.stateCode || ""),
    country: String(a.country || a.countryCode || "US"),
    zip: String(a.zip || a.postalCode || ""),
    cityCode: String(a.cityCode || a.city || ""),
    stateCode: String(a.stateCode || a.state || ""),
    countryCode: String(a.countryCode || a.country || "US"),
  };
}

/** POST /signage/:created_by_id/calculate-tax  */
export async function calcProductsTaxManual(
  token: string,
  payload: ProductTaxBatchInput
): Promise<ProductTaxLineResult[]> {
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const usertoken = payload.token
  const dest = toDestinationAddress_ForProducts(payload?.destination);
  // console.log("toDestinationAddress_ForProducts", dest,payload);
  const body = {
    created_by_id: payload.createdById,
    destination_address: dest,
    items: (payload.items || []).map(it => ({
      product_id: it.product_id,
      variant_id: it.variant_id,
      qty: Number(it.quantity || 0),
      unit_price: Number(it.unitPrice || 0)
    })),
  };
  const url = `${process.env.REACT_APP_API_URL}/signage/${payload.supplierId || payload.createdById}/calculate-tax`;
  
  try {
    const res = await fetch(url, { method: "POST", headers: jsonHeaders(usertoken || token), body: JSON.stringify(body) });
    if (!res.ok) throw new Error("Products tax calc failed");

    const data = await res.json().catch(() => ({}));
    // console.log("calcProductsTaxManual", data);
    const lines: any[] = Array.isArray(data?.lines) ? data.lines : [];

    return lines.map((ln) => ({
      product_id: String(ln.product_id || ""),
      variant_id: String(ln.variant_id || ""),
      quantity: Number(ln.qty || ln.quantity || 0),
      unit_retail_price: Number(ln.unit_price || 0),
      tax_amount: Number(ln.tax_amount || 0),
      unit_tax_amount: Number(
        ln.unit_tax_amount ?? // prefer server field
        (Number(ln.tax_amount || 0) / Math.max(1, Number(ln.qty || 0)))
      ),
      tax_percentage: Number(ln.tax_rate || 0),
      taxable: ln?.is_taxable !== false,
    }));
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "manualtax_calc_products_tax_manual",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url,
      method: "POST",
      requestBody: body,
      additionalInfo: {
        supplierId: payload.supplierId,
        createdById: payload.createdById,
        itemCount: (payload.items || []).length,
      },
    }, usertoken || token);
    throw error;
  }
}


// —————————————————————————————————————————————————————————————
// 6) ORCHESTRATORS (call only if taxable && status === true)
//    NOTE: "type" can be: "manual" | "taxcloud" | "geera"
// —————————————————————————————————————————————————————————————
export async function calcShippingByType(
  token: string,
  type: string,
  payload:
    | { kind: "manual"; input: ManualShippingInput }
    | { kind: "ups"; input: UpsShippingInput }
): Promise<ShippingCalcResult> {
  const t = String(type || "").toLowerCase();

  if (t === "manual") {
    if (payload.kind === "manual") return calcManualShippingTax(token, payload.input); // ✅
    if (payload.kind === "ups") return calcUpsShippingTax(token, payload.input);
  }

  if (t === "taxclouds") {
    // // until TC shipping calc is wired, reuse manual implementations
    // if (payload.kind === "manual") return calcManualShippingTax(token, payload.input);
    // if (payload.kind === "ups") return calcUpsShippingTax(token, payload.input);
  }

  if (t === "geera") {
    if (payload.kind === "manual") return calcManualShippingTax(token, payload.input);
    if (payload.kind === "ups") return calcUpsShippingTax(token, payload.input);
  }

  // fallback
  return payload.kind === "manual"
    ? calcManualShippingTax(token, payload.input)
    : calcUpsShippingTax(token, payload.input);
}



// manualtax.tsx

// ---------- shared helpers ----------
type Dest =
  | {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      country?: string;
      zip?: string;
      cityCode?: string;
      stateCode?: string;
      countryCode?: string;
    }
  | null
  | undefined;

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const toManualJobDest = (a: any) => {
  const src = a?.address_details || a || {};
  return {
    addressLine1: String(src.addressLine1 || src.line1 || src.address1 || ""),
    addressLine2: String(src.addressLine2 || src.line2 || src.address2 || ""),
    city: String(src.city || src.cityCode || ""),
    state: String(src.state || src.stateCode || ""),
    country: String(src.country || src.countryCode || "US"),
    zip: String(src.zip || src.postalCode || src.postcode || ""),
    stateCode: String(src.stateCode || src.state || ""),
    countryCode: String(src.countryCode || src.country || "US"),
  };
};

// ---------- INSTALLATION (manual) ----------
/**
 * POST /signage/:createdById/jobs/calculate-tax
 * body {
 *  created_by_id, dynamic_cost_id?, job_types:["installation"],
 *  subtotal, destination_address
 * }
 * returns { data: { subtotal, tax, grand_total, tax_rate ... } }
 */
export async function postInstallationManualTax(
  token: string,
  createdById: string,
  args: {
    installation_cost: number;
    destination: any;
    dynamic_cost_id?: string | null;   // backend requires this
    dynamicCostId?: string | null;     // allow camelCase too
    job_types?: string[];              // optional
  }
): Promise<{
  installation_subtotal: number;
  installation_tax_amount: number;
  grand_total: number;
  tax_rate?: number;
  matched?: boolean;
}> {
  const API = process.env.REACT_APP_API_URL || "";
  const subtotal = Number(args?.installation_cost || 0);

  // normalize dest + dynamic cost id + job types
  const destination_address = toManualJobDest(args?.destination);
  const dynId = (args?.dynamic_cost_id ?? args?.dynamicCostId) ? String(args.dynamic_cost_id ?? args.dynamicCostId) : "";
  const jobTypes = Array.isArray(args?.job_types) && args!.job_types!.length > 0 ? args!.job_types! : ["installation"];

  // 🔐 hard guard: backend *requires* dynamic_cost_id
  if (!dynId) {
    safeConsole.warn("[postInstallationManualTax] Missing dynamic_cost_id, server will reject.");
  }

  const body = {
    created_by_id: String(createdById || ""),
    job_types: jobTypes,
    subtotal,
    destination_address,
    dynamic_cost_id: dynId,            // ✅ ALWAYS set from args
  };

  safeConsole.log("body123445", body);     // should print dynamic_cost_id: "<uuid>"

  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const url = `${API}/signage/${createdById}/jobs/calculate-tax`;
  
  // graceful fallback result
  const fallbackResult = {
    installation_subtotal: r2(subtotal),
    installation_tax_amount: 0,
    grand_total: r2(subtotal),
  };
  
  try {
    const res = await fetch(url, { method: "POST", headers: jsonHeaders(token), body: JSON.stringify(body) });

    if (!res.ok) {
      // graceful fallback
      return fallbackResult;
    }

    const j = await res.json().catch(() => ({}));
    const d = j?.data || j || {};
    return {
      installation_subtotal: r2(Number(d?.subtotal || subtotal || 0)),
      installation_tax_amount: r2(Number(d?.tax || 0)),
      grand_total: r2(Number(d?.grand_total || (subtotal + Number(d?.tax || 0)))),
      tax_rate: Number(d?.tax_rate ?? 0),
      matched: Boolean(d?.matched),
    };
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "manualtax_post_installation_manual_tax",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url,
      method: "POST",
      requestBody: body,
      additionalInfo: {
        createdById,
        dynamic_cost_id: dynId,
        jobTypes,
      },
    }, token);
    // graceful fallback on error
    return fallbackResult;
  }
}

// export async function postInstallationManualTax(
//   token: string,
//   createdById: string,
//   args: {
//     installation_cost: number;
//     destination: Dest;
//     job_types?: string[];
//     // optional: if you have a dynamic cost id from your cost endpoint
//     dynamic_cost_id?: string | null;
//   }
// ): Promise<{
//   installation_subtotal: number;
//   installation_tax_amount: number;
//   grand_total: number;
//   tax_rate?: number;
//   matched?: boolean;
// }> {
//   const API = process.env.REACT_APP_API_URL || "";
//   const subtotal = Number(args?.installation_cost || 0);
//   console.log("subtotal", subtotal);
//   const destination_address = toManualJobDest(args?.destination);
//  const dynamic_cost_id = String(args?.dynamic_cost_id);
//   const body: any = {
//     created_by_id: String(createdById || ""),
//     job_types:args?.job_types,
//     subtotal,
//     destination_address,
//    dynamic_cost_id
//   };
//   console.log("body123445", body);
//   // if (args?.dynamic_cost_id) body.dynamic_cost_id = String(args.dynamic_cost_id);

//   const url = `${API}/signage/${createdById}/jobs/calculate-tax`;
//   const res = await fetch(url, { method: "POST", headers: jsonHeaders(token), body: JSON.stringify(body) });
//   if (!res.ok) {
//     // graceful fallback
//     return {
//       installation_subtotal: r2(subtotal),
//       installation_tax_amount: 0,
//       grand_total: r2(subtotal),
//     };
//   }

//   const j = await res.json().catch(() => ({}));
//   const d = j?.data || j || {};
//   return {
//     installation_subtotal: r2(Number(d?.subtotal || subtotal || 0)),
//     installation_tax_amount: r2(Number(d?.tax || 0)),
//     grand_total: r2(Number(d?.grand_total || (subtotal + Number(d?.tax || 0)))),
//     tax_rate: Number(d?.tax_rate ?? 0),
//     matched: Boolean(d?.matched),
//   };
// }

// ---------- SPECIAL FEES (manual, multi) ----------
/**
 * POST /special-fees/calculate-tax/created-by/:id
 * body { destination_address, special_fees: [full fee objects] }
 * server returns per-fee rows with amount + tax; we sum them.
 */
export async function postSpecialFeeManualTax(
  token: string,
  createdById: string,
  args: {
    destination: Dest;
    fees: Array<{
      id: string | number;
      name: string;
      amount: number;
      taxable?: boolean;
      tax_percentage?: number | null;
    }>;
  }
): Promise<{
  specialfee_subtotal: number;
  specialfee_tax_amount: number;
  rows: Array<{
    specialfee_id: string;
    specialfee_name: string;
    specialfee_amount: number;
    specialfee_tax_percentage: number; // 0–100
    specialfee_tax_amount: number;
    specialfee_taxable: boolean;
  }>;
}> {
  const API = process.env.REACT_APP_API_URL || "";
  const destination_address = toManualJobDest(args?.destination);
  const body = {
    destination_address,
    special_fees: (args?.fees || []).map((f) => ({
      id: String(f.id ?? ""),
      name: String(f.name ?? ""),
      amount: Number(f.amount || 0),
      taxable: f.taxable !== false,
      // pass-through any percentages if you collect them per-fee
      ...(f.tax_percentage != null ? { tax_percentage: Number(f.tax_percentage) } : {}),
    })),
  };

  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const url = `${API}/special-fees/calculate-tax/organizations/${createdById}`;
  
  // default empty/zero
  const zeroRows = (args?.fees || []).map((f, i) => ({
    specialfee_id: String(f.id ?? `fee-${i}`),
    specialfee_name: String(f.name ?? `Special Fee ${i + 1}`),
    specialfee_amount: Number(f.amount || 0),
    specialfee_tax_percentage: 0,
    specialfee_tax_amount: 0,
    specialfee_taxable: f.taxable !== false,
  }));
  
  // fallback result
  const fallbackResult = () => {
    const fSub = zeroRows.reduce((s, r) => s + r.specialfee_amount, 0);
    return { specialfee_subtotal: r2(fSub), specialfee_tax_amount: 0, rows: zeroRows };
  };

  try {
    const res = await fetch(url, { method: "POST", headers: jsonHeaders(token), body: JSON.stringify(body) });

    if (!res.ok) {
      return fallbackResult();
    }

    const data = await res.json().catch(() => []);
    const rows = (Array.isArray(data) ? data : []).map((r: any) => ({
      specialfee_id: String(r.specialfee_id ?? r.fee_id ?? r.id ?? ""),
      specialfee_name: String(r.specialfee_name ?? r.fee_name ?? r.name ?? ""),
      specialfee_amount: Number(r.specialfee_amount ?? r.fee_amount ?? r.amount ?? 0),
      specialfee_tax_percentage: Number(r.specialfee_tax_percentage ?? r.tax_rate ?? 0),
      specialfee_tax_amount: Number(r.specialfee_tax_amount ?? r.tax_amount ?? 0),
      specialfee_taxable: (r.tax_amount ?? 0) > 0 || (r.tax_rate ?? 0) > 0,
    }));

    const fSub = rows.reduce((s, r) => s + Number(r.specialfee_amount || 0), 0);
    const fTax = rows.reduce((s, r) => s + Number(r.specialfee_tax_amount || 0), 0);
    return { specialfee_subtotal: r2(fSub), specialfee_tax_amount: r2(fTax), rows };
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "manualtax_post_special_fee_manual_tax",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url,
      method: "POST",
      requestBody: body,
      additionalInfo: {
        createdById,
        feeCount: (args?.fees || []).length,
      },
    }, token);
    // graceful fallback on error
    return fallbackResult();
  }
}



export const SpecialFeeManualTax = postSpecialFeeManualTax;

/** Small router: pick the right special-fee calc by active type */
export async function calcSpecialFeesByType(
  token: string,
  type: string,
  inputs: SpecialFeeInput[]
): Promise<SpecialFeeCalcResult[]> {
  const tt = String(type || "").toLowerCase() as "manual" | "taxcloud" | "geera";
  return calcSpecialFeeTax(token, tt, inputs);
}

export const calspecicalfee = calcSpecialFeesByType;
// keep the “by type” wrapper consistent with the rest of manualtax helpers
export async function calcProductsTaxByType(
  token: string,
  type: "manual" | "taxcloud" | "geera",
  input: ProductTaxBatchInput
): Promise<ProductTaxLineResult[]> {
  const t = String(type || "").toLowerCase();
  // For now all paths use manual impl; change here if/when TC/Geera endpoints exist
  return calcProductsTaxManual(token, input);
}
